<template>
  <b-modal
    :active="true"
    aria-role="dialog"
    aria-modal
    has-modal-card
    trap-focus
    scroll="keep"
    full-screen
    class="dij-modal-dynamic-width"
  >
    <b-loading :is-full-page="true" :active="isLoading" :can-cancel="false">
    </b-loading>
    <ocr-modal
      :active="activeOCR"
      @close="() => (activeOCR = false)"
      :tool="tool"
      @newRatRows="addRATData"
      :file="ocrFile"
      :isMinor="activeTab === 1"
    />

    <DocumentsModal
      v-if="isDocumentModalVisible"
      :isDocumentModalVisible="isDocumentModalVisible"
      @close="closeDocumentModal"
      :selectMode="true"
      @selectFile="onSelectDocumentModal"
    />

    <b-modal
      v-if="isCameraVisible"
      :active="true"
      class="dij-ra-camera-modal"
      full-screen
    >
      <camera @cancel="hideCamera" @ok="processCamera" />
    </b-modal>

    <div class="modal-card">
      <header class="modal-card-head">
        <div>
          {{ $t('repair_assessment') }}
        </div>
        <div class="dij-ra-status">
          <b-button
            :loading="waitingForRead"
            icon-left="refresh"
            @click="refresh"
          ></b-button>
          <b-button
            class="dij-ra-save-status"
            :loading="waitingForWrite"
            :type="waitingForWrite ? 'is-warning' : 'is-success'"
            :icon-left="waitingForWrite ? '' : 'check-circle'"
            >{{ $t('saved') }}</b-button
          >
        </div>
      </header>
      <section class="modal-card-body">
        <div class="flex-container">
          <div class="upper-buttons">
            <template v-if="isRepairAssessmentTemplateSearchVisible">
              <b-input
                v-if="!isComponentSelectionVisible"
                :placeholder="$t('search_ellipsis')"
                type="search"
                icon="magnify"
                v-model="repairAssessmentTemplateSearchString"
                @keyup.native="handleRepairAssessmentTemplateSearch"
              >
              </b-input>
              <div v-else class="dij-ra-selection-title">
                {{ $t('repair_assessment_select_components') }}
                <span v-if="selectionTemplate">
                  &mdash; {{ selectionTemplate.title }}
                </span>
              </div>
            </template>
            <template v-else>
              <OcrFileSelect
                @onFileChange="onFileChangeOCR"
                @onDocumentsModal="showDocumentsModal"
                @showCamera="showCamera"
                :disabled="readOnly"
              />
              <b-button
                icon-left="drag-variant"
                :type="moveMode ? 'is-success' : ''"
                :disabled="readOnly"
                @click="handleMove"
                :class="{ 'is-disabled': readOnly }"
              >
                {{ $t('move') }}
              </b-button>

              <b-dropdown
                aria-role="list"
                :disabled="readOnly || moveMode || computedIsOffline"
              >
                <template #trigger="{ active }">
                  <b-button
                    icon-left="delete"
                    type="is-danger"
                    :label="$t('delete')"
                    :icon-right="active ? 'menu-up' : 'menu-down'"
                    data-testid="rat-delete-dropdown-trigger"
                  />
                </template>

                <b-dropdown-item
                  aria-role="listitem"
                  icon-left="delete"
                  type="is-danger"
                  data-testid="rat-delete-major"
                  @click="handleDeleteAll('major')"
                  >{{ $t('shared.major_components') }}</b-dropdown-item
                >
                <b-dropdown-item
                  aria-role="listitem"
                  icon-left="delete"
                  type="is-danger"
                  data-testid="rat-delete-minor"
                  @click="handleDeleteAll('minor')"
                  >{{ $t('shared.minor_components') }}</b-dropdown-item
                >
                <b-dropdown-item
                  aria-role="listitem"
                  icon-left="delete"
                  type="is-danger"
                  data-testid="rat-delete-all"
                  @click="handleDeleteAll('all')"
                  >{{ $t('delete_all') }}</b-dropdown-item
                >
              </b-dropdown>

              <b-dropdown aria-role="list" :disabled="readOnly || moveMode">
                <template #trigger="{ active }">
                  <b-button
                    icon-left="upload"
                    type="is-info"
                    :label="$t('import')"
                    :icon-right="active ? 'menu-up' : 'menu-down'"
                  />
                </template>

                <b-dropdown-item
                  :disabled="computedIsOffline"
                  aria-role="listitem"
                  @click="showRepairAssessmentTemplateSearch"
                  >{{ $t('import_editor_template') }}</b-dropdown-item
                >
                <input
                  ref="importAdd"
                  type="file"
                  class="hidden"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  @change="handleRepairAssessmentImportAdd"
                />
                <b-dropdown-item
                  aria-role="listitem"
                  @click="$refs.importAdd.click()"
                  >{{ $t('import_add_excel_file') }}</b-dropdown-item
                >
              </b-dropdown>
              <b-button
                icon-left="download"
                type="is-info"
                :disabled="moveMode"
                @click="exportRepairAssessment"
              >
                {{ $t('download_excel_file') }}
              </b-button>
            </template>
          </div>
          <div
            class="dij-ra-content"
            :class="{
              'dij-ra-content--scroll': isRepairAssessmentTemplateSearchVisible,
            }"
            @scroll="handleTemplateScroll"
          >
            <div
              v-if="isLoadingTemplates"
              class="dij-ra-loading-overlay"
              data-testid="rat-templates-loading"
            >
              <b-loading
                :is-full-page="false"
                :can-cancel="false"
                :active="true"
              ></b-loading>
              <div class="dij-ra-loading-overlay-text">
                {{ $t('repair_assessment_loading_templates') }}
              </div>
            </div>
            <b-table
              key="ra_template_search_table"
              :data="templates"
              v-if="
                isRepairAssessmentTemplateSearchVisible &&
                !isComponentSelectionVisible
              "
              :selected.sync="selectedTemplate"
            >
              <template v-slot="props">
                <b-table-column
                  field="product_line"
                  :label="$t('product_line')"
                >
                  {{ props.row.product_line }}
                </b-table-column>

                <b-table-column field="title" :label="$t('title')">
                  {{ props.row.title }}
                </b-table-column>

                <b-table-column
                  field="original_oem"
                  :label="$t('original_OEM')"
                >
                  {{ props.row.original_oem }}
                </b-table-column>

                <b-table-column field="frame_type" :label="$t('frame_type')">
                  {{ props.row.frame_type }}
                </b-table-column>

                <b-table-column field="model" :label="$t('model')">
                  {{ props.row.model }}
                </b-table-column>

                <b-table-column field="part_number" :label="$t('part_number')">
                  {{ props.row.part_number }}
                </b-table-column>

                <b-table-column field="location" :label="$t('location')">
                  {{ props.row.location }}
                </b-table-column>

                <b-table-column field="created_by" :label="$t('created_by')">
                  {{ props.row.created_by }}
                </b-table-column>

                <b-table-column field="date" :label="$t('date')">
                  {{ props.row.date }}
                </b-table-column>
              </template>
              <template #empty>
                <div
                  class="dij-ra-empty-results"
                  v-if="!isLoadingTemplates && !isFetchingTemplates"
                >
                  {{ $t('repair_assessment_no_templates') }}
                </div>
              </template>
            </b-table>
            <div
              class="dij-ra-load-more"
              v-if="isFetchingTemplates && templates.length"
            >
              <b-icon icon="loading" custom-class="mdi-spin"></b-icon>
              <span>{{ $t('loading') }}</span>
            </div>
            <div
              v-if="isComponentSelectionVisible"
              class="dij-ra-selection"
              data-testid="rat-component-selection"
            >
              <b-tabs
                type="is-boxed"
                :expanded="false"
                v-model="selectionActiveTab"
              >
                <b-tab-item
                  :label="$t('shared.major_components')"
                  icon="engine"
                  data-testid="rat-selection-tab-major"
                >
                  <div
                    class="dij-ra-empty-results"
                    v-if="selectionMajorRows.length === 0"
                  >
                    {{ $t('repair_assessment_empty_readonly') }}
                  </div>
                  <b-table
                    v-else
                    :data="selectionMajorRows"
                    checkable
                    :checked-rows.sync="selectedMajorRows"
                    custom-row-key="id"
                    data-testid="rat-selection-major"
                  >
                    <template v-slot="props">
                      <b-table-column field="level" :label="$t('level')">
                        {{ props.row.level }}
                      </b-table-column>
                      <b-table-column field="assembly" :label="$t('assembly')">
                        {{ props.row.assembly }}
                      </b-table-column>
                      <b-table-column
                        field="itemNumber"
                        :label="$t('item_number')"
                      >
                        {{ props.row.itemNumber }}
                      </b-table-column>
                      <b-table-column field="partName" :label="$t('part_name')">
                        {{ props.row.partName }}
                      </b-table-column>
                      <b-table-column
                        field="partNumber"
                        :label="$t('part_number')"
                      >
                        {{ props.row.partNumber }}
                      </b-table-column>
                      <b-table-column
                        field="serialNumber"
                        :label="$t('serialNumber')"
                      >
                        {{ props.row.serialNumber }}
                      </b-table-column>
                      <b-table-column field="quantity" :label="$t('quantity')">
                        {{ props.row.quantity }}
                      </b-table-column>
                    </template>
                  </b-table>
                </b-tab-item>
                <b-tab-item
                  :label="$t('shared.minor_components')"
                  icon="screw-flat-top"
                  data-testid="rat-selection-tab-minor"
                >
                  <div
                    class="dij-ra-empty-results"
                    v-if="selectionMinorRows.length === 0"
                  >
                    {{ $t('repair_assessment_empty_readonly') }}
                  </div>
                  <b-table
                    v-else
                    :data="selectionMinorRows"
                    checkable
                    :checked-rows.sync="selectedMinorRows"
                    custom-row-key="id"
                    data-testid="rat-selection-minor"
                  >
                    <template v-slot="props">
                      <b-table-column field="assembly" :label="$t('assembly')">
                        {{ props.row.assembly }}
                      </b-table-column>
                      <b-table-column
                        field="itemNumber"
                        :label="$t('item_number')"
                      >
                        {{ props.row.itemNumber }}
                      </b-table-column>
                      <b-table-column field="partName" :label="$t('part_name')">
                        {{ props.row.partName }}
                      </b-table-column>
                      <b-table-column
                        field="partNumber"
                        :label="$t('part_number')"
                      >
                        {{ props.row.partNumber }}
                      </b-table-column>
                      <b-table-column field="quantity" :label="$t('quantity')">
                        {{ props.row.quantity }}
                      </b-table-column>
                    </template>
                  </b-table>
                </b-tab-item>
              </b-tabs>
            </div>
            <b-tabs
              type="is-boxed"
              :expanded="false"
              v-model="activeTab"
              v-if="!isRepairAssessmentTemplateSearchVisible"
            >
              <b-tab-item
                label="Major Components"
                icon="engine"
                class="b-tab-item-scroll"
                data-testid="rat-tab-major"
              >
                <repair-assessment
                  ref="repairAssessment"
                  v-model="repairAssessment"
                  :read-only="readOnly"
                  :tool="tool"
                  :move-mode="moveMode"
                />
              </b-tab-item>
              <b-tab-item
                label="Minor Components"
                data-testid="rat-tab-minor"
                icon="screw-flat-top"
                class="b-tab-item-scroll"
              >
                <repair-assessment
                  ref="repairAssessmentMinor"
                  v-model="repairAssessmentMinor"
                  :isMinorComponents="true"
                  :read-only="readOnly"
                  :tool="tool"
                  :move-mode="moveMode"
                />
              </b-tab-item>
            </b-tabs>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot dij-actions">
        <template
          v-if="
            isRepairAssessmentTemplateSearchVisible &&
            !isComponentSelectionVisible
          "
        >
          <b-button @click="hideRepairAssessmentTemplateSearch">
            {{ $t('back') }}
          </b-button>
          <b-button
            type="is-primary"
            :disabled="!isTemplateSelected()"
            data-testid="rat-import-next-btn"
            @click="openComponentSelection"
          >
            {{ $t('next') }}
          </b-button>
        </template>
        <template v-if="isComponentSelectionVisible">
          <b-button @click="backToTemplateList">
            {{ $t('back') }}
          </b-button>
          <b-button
            type="is-primary"
            :disabled="selectedComponentCount === 0"
            data-testid="rat-import-selected-btn"
            @click="handleImportSelected"
          >
            {{ $t('import_add') }}
            <template v-if="selectedComponentCount">
              ({{ selectedComponentCount }})
            </template>
          </b-button>
        </template>
        <template v-if="!isRepairAssessmentTemplateSearchVisible">
          <div class="dij-ra-left-actions">
            <b-button
              type="is-primary"
              icon-right="plus"
              :disabled="readOnly"
              data-testid="rat-add-row-btn"
              @click="addRow"
            />
          </div>
          <div class="dij-ra-right-actions">
            <b-button data-testid="rat-close-btn" @click="close">
              {{ $t('close') }}
            </b-button>
          </div>
        </template>
      </footer>
    </div>
  </b-modal>
</template>
<script>
import { v4 } from 'uuid';
import client from '../../config/api';
import RepairAssessment from './RepairAssessment';
import RepairAssessmentMixin from '../../mixins/RepairAssessmentMixin';
import OcrFileSelect from './OcrFileSelect';
import OcrModal from './OcrModal';
import DocumentsModal from '../Shared/DocumentsModal';
import Camera from './Camera';
import OfflineMixin from '../../mixins/OfflineMixin';
import { GET_CURRENT_JOB } from '../../store/jobs/getters/getterTypes';
import { ADD_REPAIR_ASSESSMENT_ROW } from '../../store/jobs/actions/actionTypes';
import {
  WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION,
  WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION,
} from '../../store/websocket/actions/actionTypes';

const uuidv4 = v4;

export default {
  name: 'repair-assessment-dialog',
  props: ['readOnly', 'tool'],
  emits: ['close'],
  mixins: [RepairAssessmentMixin, OfflineMixin],
  data() {
    return {
      repairAssessment: [],
      repairAssessmentMinor: [],
      repairAssessmentTemplateSearchString: '',
      isRepairAssessmentTemplateSearchVisible: false,
      isComponentSelectionVisible: false,
      isLoadingTemplates: false,
      templates: [],
      templatesLastKey: undefined,
      isFetchingTemplates: false,
      selectedTemplate: null,
      selectionTemplate: null,
      selectionActiveTab: 0,
      selectedMajorRows: [],
      selectedMinorRows: [],
      moveMode: false,
      activeTab: 0,
      activeOCR: false,
      ocrFile: null,
      isDocumentModalVisible: false,
      isCameraVisible: false,
      isLoading: false,
    };
  },
  components: {
    RepairAssessment,
    OcrModal,
    OcrFileSelect,
    DocumentsModal,
    Camera,
  },
  computed: {
    job() {
      return this.$store.getters[GET_CURRENT_JOB];
    },
    waitingForRead() {
      return this.$store.state.waitingForRead;
    },
    waitingForWrite() {
      return this.$store.state.waitingForWrite;
    },
    selectionMajorRows() {
      if (!this.selectionTemplate) {
        return [];
      }
      return this.selectionTemplate.repairAssessment.filter(
        (row) => !row.isMinor
      );
    },
    selectionMinorRows() {
      if (!this.selectionTemplate) {
        return [];
      }
      return this.selectionTemplate.repairAssessment.filter(
        (row) => row.isMinor
      );
    },
    selectedComponentCount() {
      return this.selectedMajorRows.length + this.selectedMinorRows.length;
    },
  },
  mounted() {
    this.initRepairAssessment();
  },
  created() {
    const wsMappings = {
      ratRowCreated: this.refresh,
      ratRowDeleted: this.refresh,
      ratRowUpdated: this.refresh,
    };

    Object.keys(wsMappings).forEach(async (key) => {
      await this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
        action: key,
        callback: wsMappings[key],
      });
    });
  },
  beforeDestroy() {
    const wsMappings = {
      ratRowCreated: this.refresh,
      ratRowDeleted: this.refresh,
      ratRowUpdated: this.refresh,
    };

    Object.keys(wsMappings).forEach(async (key) => {
      await this.$store.dispatch(WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION, {
        action: key,
        callback: wsMappings[key],
      });
    });
  },
  watch: {
    'job.repairAssessment': {
      handler() {
        this.initRepairAssessment();
      },
      immediate: true,
    },
  },
  methods: {
    initRepairAssessment() {
      this.repairAssessment = [];
      this.repairAssessmentMinor = [];
      if (this.job && this.job.repairAssessment) {
        this.job.repairAssessment.forEach((element) => {
          if (element.isMinor) {
            this.repairAssessmentMinor.push(
              element.id ? { ...element } : { id: uuidv4(), ...element }
            );
          } else {
            this.repairAssessment.push(
              element.id ? { ...element } : { id: uuidv4(), ...element }
            );
          }
        });
      }
    },
    async refresh() {
      const repairAssessment = await this.$store.dispatch(
        'getRepairAssessment',
        this.job
      );
      const majorRAT = repairAssessment.filter((ra) => !ra.isMinor);
      this.repairAssessment = [...majorRAT];
      const minorRAT = repairAssessment.filter((ra) => ra.isMinor);
      this.repairAssessmentMinor = [...minorRAT];
      this.job.repairAssessment = repairAssessment;
      await this.$refs.repairAssessment.refresh();
    },
    async addRATData(rows) {
      try {
        this.isLoading = true;
        await this.$refs.repairAssessment.addRATData(rows);
        await this.refresh();
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    onFileChangeOCR(file) {
      this.ocrFile = file;
      this.activeOCR = true;
    },
    showDocumentsModal() {
      this.isDocumentModalVisible = true;
    },
    closeDocumentModal() {
      this.isDocumentModalVisible = false;
    },
    showCamera() {
      this.isCameraVisible = true;
    },

    hideCamera() {
      this.isCameraVisible = false;
    },
    processCamera(canvas) {
      this.hideCamera();
      const base64String = canvas.toDataURL();
      const byteCharacters = atob(base64String.split(',')[1]);
      const byteArrays = [];
      const fileType = 'image/png';
      for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
        const slice = byteCharacters.slice(offset, offset + 1024);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }
      const blob = new Blob(byteArrays, { type: fileType });
      const file = new File([blob], 'image', { type: fileType });
      this.ocrFile = file;
      this.activeOCR = true;
    },
    async onSelectDocumentModal({ url: fileUrl, fileName, fileType }) {
      try {
        this.isLoading = true;
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: fileType });
        this.ocrFile = file;
        this.activeOCR = true;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    handleMove() {
      if (this.moveMode) {
        this.$store
          .dispatch('unlockRepairAssessment', {
            job: this.job,
            tool: this.tool,
          })
          .then(() => {
            this.moveMode = !this.moveMode;
          });
      } else {
        this.$store
          .dispatch('lockRepairAssessment', { job: this.job, tool: this.tool })
          .then(() => {
            this.moveMode = !this.moveMode;
          })
          .catch((error) => {
            this.$buefy.toast.open(error.response.data.message);
          });
      }
    },
    message(typeToDelete) {
      let message = '';
      if (typeToDelete === 'major') {
        message = this.$t('delete_major_message');
      } else if (typeToDelete === 'minor') {
        message = this.$t('delete_minor_message');
      } else {
        message = this.$t('delete_all_message');
      }
      return message;
    },
    handleDeleteAll(typeToDelete) {
      this.$buefy.dialog.confirm({
        message: this.message(typeToDelete),
        onConfirm: () => {
          this.$store
            .dispatch('deleteRepairAssessment', {
              job: this.job,
              tool: this.tool,
              typeToDelete,
            })
            .then(() => {
              this.refresh();
            });
        },
      });
    },

    async showRepairAssessmentTemplateSearch() {
      this.isRepairAssessmentTemplateSearchVisible = true;
      this.templates = [];
      this.templatesLastKey = undefined;
      this.selectedTemplate = null;
      await this.fetchTemplatePage(true);
    },
    /**
     * Fetch a single page of RAT templates. On the first page no lastKey is
     * sent; subsequent pages pass the lastKey returned by the previous
     * response. Fetching stops once the backend stops returning a lastKey.
     */
    async fetchTemplatePage(isFirstPage = false) {
      if (this.isFetchingTemplates) {
        return;
      }
      if (!isFirstPage && !this.templatesLastKey) {
        return;
      }

      const query = {
        type: 'editor',
        repairAssessment: true,
        limitItems: 50,
        search: [this.repairAssessmentTemplateSearchString],
      };
      if (!isFirstPage) {
        query.lastKey = this.templatesLastKey;
      }

      try {
        if (isFirstPage) {
          this.isLoadingTemplates = true;
        }
        this.isFetchingTemplates = true;
        const { data } = await client.get('/job', { params: { query } });
        (data.jobs || [])
          .filter(
            (job) =>
              job.hasRepairAssessment === undefined || job.hasRepairAssessment
          )
          .forEach((job) => {
            this.templates.push(this.mapJobToTemplate(job));
          });
        this.templatesLastKey = data.lastKey;
      } catch (error) {
        console.error('Error fetching RAT templates:', error);
        this.$buefy.toast.open({
          duration: 5000,
          message: this.$t('template_list.failed_to_fetch_listings'),
          type: 'is-danger',
        });
      } finally {
        this.isFetchingTemplates = false;
        if (isFirstPage) {
          this.isLoadingTemplates = false;
        }
      }
    },
    loadMoreTemplates() {
      if (this.isFetchingTemplates || !this.templatesLastKey) {
        return;
      }
      this.fetchTemplatePage(false);
    },
    handleTemplateScroll(event) {
      if (
        !this.isRepairAssessmentTemplateSearchVisible ||
        this.isComponentSelectionVisible
      ) {
        return;
      }
      const el = event.target;
      const threshold = 150;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
        this.loadMoreTemplates();
      }
    },
    mapJobToTemplate(job) {
      return {
        id: job._id,
        title: job.title,
        product_line: this.getAndFormatDropValues(job.workOrderName),
        original_oem: this.getAndFormatDropValues(job.brand),
        frame_type: this.getAndFormatDropValues(job.frameType),
        model: this.getAndFormatDropValues(job.model),
        part_number: job.userEnteredPartNumber,
        location: this.getJobLocation(job),
        created_by: this.getCreatedBy(job),
        date: this.getDateString(job),
        repairAssessment: [],
        isRaLoaded: false,
        isRaLoading: false,
      };
    },
    async loadTemplateRepairAssessment(row) {
      if (!row || row.isRaLoaded || row.isRaLoading) {
        return;
      }

      row.isRaLoading = true;
      try {
        const repairAssessment = await this.$store.dispatch(
          'getRepairAssessmentByJobId',
          row.id
        );
        // Sanitize to the clean shape the app creates for new rows. The raw
        // rows from /ra/ carry inspector/publisher artifacts (changeLog,
        // original* fields, position, locks) and inconsistent types (e.g.
        // level: '') that don't belong in a fresh import and make the add
        // request fail. This also assigns fresh ids so imported rows never
        // collide with the source template's rows.
        row.repairAssessment = (repairAssessment || []).map((element) =>
          this.sanitizeTemplateRow(element)
        );
        row.isRaLoaded = true;
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          duration: 5000,
          message: this.$t('repair_assessment_template_load_failed'),
          type: 'is-danger',
        });
      } finally {
        row.isRaLoading = false;
      }
    },
    sanitizeTemplateRow(element) {
      const quantity = Number(element.quantity);
      const base = {
        id: uuidv4(),
        assembly: element.assembly ?? '',
        itemNumber: element.itemNumber ?? '',
        partName: element.partName ?? '',
        partNumber: element.partNumber ?? '',
        quantity: Number.isFinite(quantity) ? quantity : 1,
      };

      if (element.isMinor) {
        return { ...base, isMinor: true };
      }

      const level = Number(element.level);
      return {
        ...base,
        level: Number.isFinite(level) ? level : 0,
        serialNumber: element.serialNumber ?? '',
      };
    },
    hideRepairAssessmentTemplateSearch() {
      this.isRepairAssessmentTemplateSearchVisible = false;
      this.isComponentSelectionVisible = false;
      this.selectedTemplate = null;
      this.selectionTemplate = null;
      this.selectedMajorRows = [];
      this.selectedMinorRows = [];
      this.templates = [];
      this.templatesLastKey = undefined;
    },
    isTemplateSelected() {
      return this.selectedTemplate !== null;
    },
    cancelRepairAssessmentChange() {
      this.repairAssessment = [];
      if (this.job.repairAssessment) {
        this.job.repairAssessment.forEach((element) =>
          this.repairAssessment.push(element)
        );
      }
      this.$emit('close');
    },
    handleRepairAssessmentTemplateSearch(event) {
      if (event.key === 'Enter') {
        this.selectedTemplate = null;
        this.showRepairAssessmentTemplateSearch();
      }
    },
    async openComponentSelection() {
      const template = this.selectedTemplate;
      if (!template) {
        return;
      }

      this.isLoading = true;
      try {
        // Lazy-load the template's RAT (major + minor) for the selection step.
        await this.loadTemplateRepairAssessment(template);

        // Load failed (it already surfaced its own toast) - stop here.
        if (!template.isRaLoaded) {
          return;
        }

        if (!template.repairAssessment.length) {
          this.$buefy.toast.open({
            duration: 5000,
            message: this.$t('repair_assessment_template_empty'),
            type: 'is-warning',
          });
          return;
        }

        this.selectionTemplate = template;
        // Preselect everything so importing all is one click away.
        this.selectedMajorRows = [...this.selectionMajorRows];
        this.selectedMinorRows = [...this.selectionMinorRows];
        // Land on the tab that actually has rows.
        this.selectionActiveTab = this.selectionMajorRows.length ? 0 : 1;
        this.isComponentSelectionVisible = true;
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          duration: 5000,
          message:
            error.response?.data?.message ||
            this.$t('repair_assessment_template_load_failed'),
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    backToTemplateList() {
      this.isComponentSelectionVisible = false;
      this.selectionTemplate = null;
      this.selectedMajorRows = [];
      this.selectedMinorRows = [];
    },
    async handleImportSelected() {
      const rows = [...this.selectedMajorRows, ...this.selectedMinorRows];
      if (!rows.length) {
        return;
      }

      this.isLoading = true;
      try {
        await this.$store.dispatch(ADD_REPAIR_ASSESSMENT_ROW, {
          job: this.job,
          rows,
          tool: this.tool,
        });

        this.hideRepairAssessmentTemplateSearch();

        // Re-read the persisted RAT and re-split into major/minor. Done from
        // the store rather than this.refresh(), because the grid refs are not
        // mounted while the template-search view is open.
        const repairAssessment = await this.$store.dispatch(
          'getRepairAssessment',
          this.job
        );
        this.repairAssessment = repairAssessment.filter((ra) => !ra.isMinor);
        this.repairAssessmentMinor = repairAssessment.filter(
          (ra) => ra.isMinor
        );
        this.job.repairAssessment = repairAssessment;
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          duration: 5000,
          message:
            error.response?.data?.message ||
            this.$t('repair_assessment_template_load_failed'),
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    close() {
      if (!this.readOnly) {
        if (!this.waitingForWrite) {
          this.$store
            .dispatch('unlockRepairAssessment', {
              job: this.job,
              tool: this.tool,
            })
            .then((repairAssessment) => {
              this.job.repairAssessment = repairAssessment;
            });
        }

        this.job.repairAssessment = [
          ...this.repairAssessment,
          ...this.repairAssessmentMinor,
        ];
      }
      this.$emit('close');
    },
    async addRow() {
      this.isLoading = true;
      await this.$refs.repairAssessment.addRow(this.activeTab);
      await this.refresh();
      this.isLoading = false;
    },
    getAndFormatDropValues(dropType) {
      let namesFormated = [];
      if (typeof dropType === 'undefined' || dropType === null) return '';
      if (typeof dropType !== 'string' && dropType.length > 1) {
        dropType.forEach((type) => {
          namesFormated.push(type.text);
        });
      } else if (typeof dropType === 'string') {
        namesFormated = dropType;
      } else if (typeof dropType[0] !== 'undefined') {
        namesFormated = dropType[0].text;
      } else {
        namesFormated = '';
      }
      if (typeof namesFormated === 'string') {
        return namesFormated;
      }
      if (Array.isArray(namesFormated)) {
        return namesFormated.join(', ');
      }
      return '';
    },
    getJobLocation(job) {
      if (job.prodCreatedByUser && job.prodCreatedByUser.location) {
        const { location } = job.prodCreatedByUser;
        if (typeof location === 'object') {
          return job.prodCreatedByUser.location.siteName;
        }
        return this.$store.getters.siteName(location);
      }
      return '';
    },
    getCreatedBy(job) {
      if (job.prodCreatedByUser && job.prodCreatedByUser.fullname) {
        return job.prodCreatedByUser.fullname;
      }
      if (job.modifier && job.modifier.fullname) {
        return job.modifier.fullname;
      }
      return '';
    },
    getDateString(job) {
      let { date } = job;
      const { prodCreatedByUser } = job;
      if (!date) {
        date = prodCreatedByUser.timestamp;
      }
      if (
        !date ||
        (typeof date !== 'number' && Object.keys(date).length === 0)
      ) {
        date = new Date().getTime();
      }
      const dateObject = new Date(parseFloat(date));
      const dateString = `${
        dateObject.getMonth() + 1
      }-${dateObject.getDate()}-${dateObject.getFullYear()}`;

      return dateString;
    },
  },
};
</script>

<style lang="scss" scoped>
.b-tabs {
  width: 100% !important;
  height: 100% !important;
}

.b-tab-item-scroll {
  height: 100%;
}

.flex-container {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
}

::v-deep div.animation-content.modal-content {
  display: flex;
  flex-direction: column;
}
section.modal-card-body {
  overflow: hidden;
}
.upper-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.dij-ra-selection-title {
  font-weight: 600;
  align-self: center;
  margin-right: auto;
}
.dij-ra-selection-section {
  margin-bottom: 20px;
}
.dij-ra-selection-heading {
  font-weight: 600;
  margin-bottom: 8px;
}
.dij-ra-table-loading {
  position: relative;
  height: 100px;
}
.dij-ra-empty-results {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  color: rgb(100, 100, 100);
}
.dij-ra-content {
  margin-left: 10px;
  margin-right: 10px;
  height: calc(100% - 50px);
  position: relative;
}
.dij-ra-content--scroll {
  overflow-y: auto;
}
.dij-ra-content--scroll ::v-deep .b-table tbody tr {
  cursor: pointer;
}
.dij-ra-loading-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
}
.dij-ra-loading-overlay-text {
  position: absolute;
  top: calc(50% + 2.5em);
  width: 100%;
  text-align: center;
  color: rgb(100, 100, 100);
  z-index: 1;
}
.dij-ra-load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: #7a7a7a;
}
.dij-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
// hide modal close button
::v-deep button.modal-close.is-large {
  display: none;
}
// Remove gap between button and dropdown
::v-deep div.dropdown-trigger {
  height: unset;
}
// Set max width and correct font-size
div.dij-modal-dynamic-width {
  font-size: 16px;
  ::v-deep div.animation-content {
    max-width: 100%;
  }
}
// Set max heigth
section.modal-card-body {
  max-height: 80vh;
  height: 80vh;
}
.dij-ra-right-actions {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.modal-card-head {
  display: flex;
  justify-content: space-between;
}
.dij-ra-status {
  display: flex;
  gap: 10px;

  .dij-ra-save-status {
    border-radius: 0;
    cursor: unset;
    background: white;
    color: green;
  }
}
</style>
<style>
.tab-content {
  height: 100% !important;
}
</style>
